import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { BrandLogo, useBranding } from "@/components/BrandLogo";
import { NeedHelp } from "@/components/NeedHelp";
import { Button } from "@/components/ui/button";
import { CheckCircle2, FileText, ArrowRight, LayoutDashboard, ClipboardList } from "lucide-react";

function Row({ label, value, testid }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-slate-100 py-3 last:border-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <span className="text-sm text-slate-500">{label}</span>
      <span data-testid={testid} className="text-sm font-semibold text-slate-900 sm:max-w-[60%] sm:text-right">{value || "—"}</span>
    </div>
  );
}

export default function ClientOrderConfirmation() {
  const { id } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const brand = useBranding();
  const [order, setOrder] = useState(loc.state?.order || null);
  const [docs, setDocs] = useState(loc.state?.docs || null); // [{id, filename}]

  useEffect(() => {
    if (!order) {
      api.get(`/orders/${id}`).then(({ data }) => setOrder(data)).catch(() => setOrder(false));
    }
    if (!docs) {
      api.get(`/orders/${id}/documents`)
        .then(({ data }) => setDocs((data.documents || [])
          .filter((d) => d.category === "documents_for_service")
          .map((d) => ({ id: d.id, filename: d.original_filename }))))
        .catch(() => setDocs([]));
    }
  }, [id, order, docs]);

  if (order === false) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center" data-testid="confirmation-not-found">
        <p className="text-slate-600">We couldn't load this order.</p>
        <Button className="mt-4" onClick={() => nav("/client")} data-testid="confirmation-back-dashboard">Return to Dashboard</Button>
      </div>
    );
  }
  if (!order) return null;

  const a = order.service_address || {};
  const cityLine = [a.city, a.state].filter(Boolean).join(", ") + (a.zip ? ` ${a.zip}` : "");
  const address = [a.street, cityLine].map((s) => (s || "").trim()).filter(Boolean).join(", ") || "—";
  const deadline = order.deadline
    ? new Date(order.deadline).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : "No deadline specified";
  const docList = docs || [];

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="order-confirmation-page">
      {/* Branded header */}
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-slate-200 bg-white p-1.5">
          <BrandLogo className="h-full w-auto" />
        </div>
        <div>
          <p data-testid="confirmation-business-name" className="font-display text-base font-extrabold leading-tight tracking-tight text-slate-900">Audit Ready Process Service</p>
          <p data-testid="confirmation-tagline" className="font-mono-arps text-[11px] text-slate-500">{brand.tagline}</p>
        </div>
      </div>

      {/* Success banner */}
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center sm:p-8" data-testid="confirmation-success-banner">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Service request received</h1>
        <p className="mt-1.5 text-sm text-slate-600">Your service order has been successfully submitted to ARPS.</p>
        <div className="mt-4 inline-flex flex-col items-center rounded-xl border border-emerald-300 bg-white px-6 py-3">
          <span className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Job Number</span>
          <span data-testid="confirmation-job-number" className="font-display text-2xl font-extrabold tracking-tight text-emerald-700">{order.job_number}</span>
        </div>
      </div>

      {/* Summary */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="confirmation-summary">
        <h2 className="font-display text-lg font-bold text-slate-900">Order summary</h2>
        <div className="mt-2">
          <Row label="Defendant / Respondent" value={order.defendant} testid="confirmation-defendant" />
          <Row label="Service address" value={address} testid="confirmation-address" />
          <Row label="Service type" value={order.service_type} testid="confirmation-service-type" />
          <Row label="Deadline" value={deadline} testid="confirmation-deadline" />
          {order.case_number ? <Row label="Case number" value={order.case_number} testid="confirmation-case-number" /> : null}
          <div className="pt-3">
            <p className="text-sm text-slate-500">Documents submitted ({docList.length})</p>
            <div className="mt-2 space-y-1.5" data-testid="confirmation-documents">
              {docList.length === 0 && <p className="text-sm text-slate-400">No documents attached</p>}
              {docList.map((d) => (
                <div key={d.id} data-testid={`confirmation-doc-${d.id}`} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  <FileText className="h-4 w-4 shrink-0 text-emerald-600" /> <span className="truncate">{d.filename}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* What happens next */}
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6" data-testid="confirmation-next-steps">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-slate-500" />
          <h2 className="font-display text-base font-bold text-slate-900">What happens next</h2>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          ARPS will review your request and begin the assignment process, matching your order with a qualified process server.
          You'll receive updates as the status changes — from assignment through each documented service attempt, GPS-verified and
          timestamped, to the final court-ready affidavit. You can track progress anytime from your dashboard.
        </p>
      </div>

      <NeedHelp jobNumber={order.job_number} />

      {/* Actions */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <Button data-testid="confirmation-view-order" onClick={() => nav(`/client/orders/${order.id || id}`)} className="flex-1 bg-slate-900 text-white hover:bg-slate-800">
          View Order <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <Button data-testid="confirmation-return-dashboard" variant="outline" onClick={() => nav("/client")} className="flex-1">
          <LayoutDashboard className="mr-2 h-4 w-4" /> Return to Dashboard
        </Button>
      </div>
    </div>
  );
}
